import { useState, useEffect } from "react";
import { WalletAddressInput } from "@/ui/components/ui/wallet-address-input";
import { VisibilityToggle } from "@/ui/components/ui/VisibilityToggle";
import {
  EthereumLogo,
  BitcoinLogo,
  SolanaLogo,
} from "@/ui/components/icons/crypto";
import {
  walletSchemas,
  ensNameSchema,
  snsNameSchema,
} from "@/schemas/profileSchemas";

export interface Wallet {
  type: "ETH" | "SOL" | "BTC";
  address: string;
  ensName?: string;
  visible: boolean;
}

export interface WalletManagementSectionProps {
  wallets: Wallet[];
  onAddWallet: (
    type: "ETH" | "SOL" | "BTC",
    address: string,
    ensName?: string
  ) => Promise<void>;
  onDeleteWallet: (type: "ETH" | "SOL" | "BTC") => Promise<void>;
  onToggleVisibility: (type: "ETH" | "SOL" | "BTC") => void;
  canAddWallet: (type: "ETH" | "SOL" | "BTC") => boolean;
  isLoading?: boolean;
}

const WALLET_TYPES = [
  { type: "ETH" as const, nameService: "ENS", label: "Ethereum" },
  { type: "SOL" as const, nameService: "SNS", label: "Solana" },
  { type: "BTC" as const, nameService: null, label: "Bitcoin" },
];

const walletIcons = {
  ETH: <EthereumLogo size={12} />,
  SOL: <SolanaLogo size={12} />,
  BTC: <BitcoinLogo size={12} />,
};

/**
 * WalletManagementSection Component
 *
 * Manages crypto wallet addresses with always-visible card layout:
 * - Primary field: wallet address
 * - Secondary field: optional ENS/SNS name
 * - Visibility toggle for public profile
 * - Delete confirmation modal
 * - Contextual help via HelpTooltip
 *
 * Features:
 * - Max one wallet per type enforcement
 * - Auto-save on blur for both fields
 * - Clear buttons for both fields
 * - Optimistic visibility updates
 * - Always-visible wallet sections (no collapse)
 *
 * @example
 * ```tsx
 * <WalletManagementSection
 *   wallets={wallets}
 *   onAddWallet={handleAddWallet}
 *   onDeleteWallet={handleDeleteWallet}
 *   onToggleVisibility={handleToggleVisibility}
 *   canAddWallet={(type) => !wallets.some(w => w.type === type)}
 * />
 * ```
 */
export function WalletManagementSection({
  wallets,
  onAddWallet,
  onDeleteWallet: _onDeleteWallet,
  onToggleVisibility,
  canAddWallet: _canAddWallet,
  isLoading = false,
}: WalletManagementSectionProps) {
  // Local state for input values during typing
  const [localValues, setLocalValues] = useState<Record<string, string>>({});

  // Validation error state
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Sync local state with backend wallet data when it changes
  // BUT: Don't sync during save operations to prevent overwriting user input
  useEffect(() => {
    if (isLoading) {
      return; // Preserve local state during save
    }

    const initialValues: Record<string, string> = {};
    wallets.forEach((w) => {
      initialValues[`${w.type}-address`] = w.address || "";
      initialValues[`${w.type}-ens`] = w.ensName || "";
    });

    setLocalValues(initialValues);
  }, [wallets, isLoading]);

  const handleAddressBlur = async (
    type: "ETH" | "SOL" | "BTC",
    address: string,
    ensName?: string
  ) => {
    const errorKey = `${type}-address`;

    // Client-side validation with Zod
    if (address) {
      // Only validate if address is not empty
      const schema = walletSchemas[type];
      const validationResult = schema.safeParse({
        type,
        address,
        ensName: ensName || null,
        visible: true, // Default value for validation
      });

      if (!validationResult.success) {
        // Extract error message for address field
        const addressError = validationResult.error.issues.find(
          (issue) => issue.path[0] === "address"
        );

        if (addressError) {
          setValidationErrors((prev) => ({
            ...prev,
            [errorKey]: addressError.message,
          }));
          return; // Stop here - don't call API
        }
      }
    }

    // Clear error if valid or empty
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });

    // Proceed with API call (existing logic)
    const wallet = wallets.find((w) => w.type === type);

    if (wallet && wallet.address !== address) {
      // Update existing wallet (allow empty to save empty value)

      await onAddWallet(type, address, ensName || wallet.ensName);
    } else if (!wallet && address) {
      // Add new wallet only if address is not empty
      await onAddWallet(type, address, ensName);
    }
  };

  const handleEnsNameBlur = async (
    type: "ETH" | "SOL" | "BTC",
    address: string,
    ensName?: string
  ) => {
    const errorKey = `${type}-ens`;
    const wallet = wallets.find((w) => w.type === type);

    if (!wallet) return; // Can't validate ENS without existing wallet

    // Clear error if ENS name is empty (not an error state)
    if (!ensName) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[errorKey];
        return next;
      });
      // Still call API to clear ENS name from backend
      await onAddWallet(type, address, ensName);
      return;
    }

    // Validate based on wallet type
    let validationResult;

    if (type === "ETH") {
      // Validate ENS name (.eth)
      validationResult = ensNameSchema.safeParse(ensName);
    } else if (type === "SOL") {
      // Validate SNS name (.sol)
      validationResult = snsNameSchema.safeParse(ensName);
    } else {
      // BTC doesn't support ENS names - should never reach here
      return;
    }

    if (!validationResult.success) {
      // Set validation error
      setValidationErrors((prev) => ({
        ...prev,
        [errorKey]: validationResult.error.issues[0].message,
      }));
      return; // Stop here - don't call API
    }

    // Clear error if valid
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });

    // Proceed with API call only if validation passed
    await onAddWallet(type, address, ensName);
  };

  const handleClear = async (type: "ETH" | "SOL" | "BTC") => {
    try {
      // Send PUT with empty address (backend handles visibility automatically)
      await onAddWallet(type, "", "");
    } catch (error) {
      console.error("Failed to clear wallet:", error);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Wallet List - Always visible sections with HelpTooltip */}
      {WALLET_TYPES.map(({ type, nameService, label }) => {
        const wallet = wallets.find((w) => w.type === type);
        return (
          <div key={type} className="flex flex-col gap-2">
            {/* Header with chain icon, label, and help tooltip */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                {walletIcons[type]}
                <span className="text-sm font-montserrat font-semibold text-gray-900">
                  Info {label}
                </span>
              </div>
            </div>

            {/* Wallet fields - always visible */}
            <div className="flex flex-col gap-2 ">
              {/* Wallet Address Input */}
              <div className="flex flex-col gap-1">
                <WalletAddressInput
                  variant="wallet-address"
                  value={
                    localValues[`${type}-address`] ?? wallet?.address ?? ""
                  }
                  placeholder="Enter wallet address"
                  showClearButton={!!wallet?.address}
                  onClear={() => wallet && handleClear(type)}
                  onChange={(e) =>
                    setLocalValues((prev) => ({
                      ...prev,
                      [`${type}-address`]: e.target.value,
                    }))
                  }
                  onBlur={(e) =>
                    handleAddressBlur(type, e.target.value, wallet?.ensName)
                  }
                />
                {/* Inline error message */}
                {validationErrors[`${type}-address`] && (
                  <span className="text-xs text-danger">
                    {validationErrors[`${type}-address`]}
                  </span>
                )}
              </div>

              {/* ENS/SNS Name Input (only for ETH and SOL) */}
              {nameService && (
                <div className="flex flex-col gap-1">
                  <WalletAddressInput
                    variant="name-service"
                    value={localValues[`${type}-ens`] ?? wallet?.ensName ?? ""}
                    label={`${nameService} :`}
                    placeholder={`Enter ${nameService} name (optional)`}
                    showClearButton={!!wallet?.ensName}
                    onClear={() =>
                      wallet && handleEnsNameBlur(type, wallet.address, "")
                    }
                    onChange={(e) =>
                      setLocalValues((prev) => ({
                        ...prev,
                        [`${type}-ens`]: e.target.value,
                      }))
                    }
                    onBlur={(e) =>
                      wallet &&
                      handleEnsNameBlur(type, wallet.address, e.target.value)
                    }
                  />
                  {/* Inline error message */}
                  {validationErrors[`${type}-ens`] && (
                    <span className="text-xs text-danger">
                      {validationErrors[`${type}-ens`]}
                    </span>
                  )}
                </div>
              )}

              {/* Visibility Toggle - Always visible, disabled when both address and ENS name are empty */}
              <VisibilityToggle
                visible={wallet?.visible || false}
                onToggle={() => wallet && onToggleVisibility(type)}
                disabled={!wallet?.address && !wallet?.ensName}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
